/**
 * Deck normalisation (docs/design/07 §4.1; research 05 §3.7): Runebound's `play.load_deck` result →
 * the engine's `DeckList`.
 *
 * - The legend is a printing (`legend_printing_id`): its card is the legend, its art is kept.
 * - The Chosen Champion is a card (`champion_card_id`); it counts toward the 40 and is *not* repeated in
 *   the `main` rows (extra copies of the same card are `main` rows).
 * - `flex` rows ("considering") are not part of the deck (owner decision Q20); rows with quantity ≤ 0 are
 *   ignored, as Runebound's readers do.
 * - Variant card ids are folded to their rules identity; each card keeps the art of the row it came
 *   from (`plain_printing` of the original id).
 * - Lines are merged by (card, printing) and sorted, so equal decks normalise identically and hash the
 *   same.
 */
import type { DeckCard, DeckEntry, DeckList } from '@play/engine';
import { z } from 'zod';
import type { Catalog } from '../catalog';
import { type DeckIssue, issue } from './issues';

export const RUNEBOUND_DECK_ZONES = ['main', 'rune', 'battlefield', 'side', 'flex'] as const;

/** The shape `play.load_deck(deck_id)` returns (research 05 §3.7). Unknown keys are ignored. */
export const RuneboundDeckSchema = z.object({
  ownerId: z.string().optional(),
  deckId: z.string().min(1),
  name: z.string(),
  format: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  legendPrintingId: z.string().nullable(),
  legendCardId: z.string().nullable().optional(),
  championCardId: z.string().nullable(),
  cards: z.array(
    z.object({
      cardId: z.string().min(1),
      zone: z.enum(RUNEBOUND_DECK_ZONES),
      quantity: z.int(),
    }),
  ),
});
export type RuneboundDeck = z.infer<typeof RuneboundDeckSchema>;

/** The engine deck list plus what normalisation could not resolve. */
export interface NormalisedDeck {
  deck: DeckList;
  /** `standard` unless the deck says otherwise (Runebound treats a null format as standard). */
  format: string;
  name: string;
  /** Problems that prevented a faithful conversion (unknown cards or printings). */
  issues: DeckIssue[];
}

function sortEntries(entries: DeckEntry[]): DeckEntry[] {
  return entries.sort((a, b) =>
    a.cardId < b.cardId
      ? -1
      : a.cardId > b.cardId
        ? 1
        : (a.printingId ?? '') < (b.printingId ?? '')
          ? -1
          : (a.printingId ?? '') > (b.printingId ?? '')
            ? 1
            : 0,
  );
}

function addEntry(into: Map<string, DeckEntry>, card: DeckCard, quantity: number): void {
  const key = `${card.cardId}\u0000${card.printingId ?? ''}`;
  const existing = into.get(key);
  if (existing) existing.quantity += quantity;
  else into.set(key, { ...card, quantity });
}

/** Converts a Runebound deck to the engine's normalised `DeckList`. */
export function normaliseDeck(input: RuneboundDeck, catalog: Catalog): NormalisedDeck {
  const issues: DeckIssue[] = [];

  const deckCard = (cardId: string, section: DeckIssue['section']): DeckCard => {
    if (!catalog.card(cardId))
      issues.push(issue('unknownCard', 'catalogue', `Unknown card ${cardId}`, cardId, section));
    return { cardId: catalog.resolveAlias(cardId), printingId: catalog.plainPrinting(cardId)?.id ?? null };
  };

  let legend: DeckCard = { cardId: input.legendCardId ?? '', printingId: null };
  if (input.legendPrintingId) {
    const printing = catalog.printing(input.legendPrintingId);
    if (printing) legend = { cardId: catalog.resolveAlias(printing.cardId), printingId: printing.id };
    else {
      issues.push(
        issue('unknownPrinting', 'catalogue', `Unknown legend printing ${input.legendPrintingId}`, null, 'legend'),
      );
      if (input.legendCardId) legend = deckCard(input.legendCardId, 'legend');
    }
  } else if (input.legendCardId) {
    legend = deckCard(input.legendCardId, 'legend');
  }
  if (!legend.cardId) issues.push(issue('legend', 'CR 103.2', 'The deck has no Champion Legend', null, 'legend'));

  const champion = input.championCardId ? deckCard(input.championCardId, 'champion') : null;

  const sections = {
    main: new Map<string, DeckEntry>(),
    rune: new Map<string, DeckEntry>(),
    battlefield: new Map<string, DeckEntry>(),
    side: new Map<string, DeckEntry>(),
  };
  const sectionName = { main: 'main', rune: 'runes', battlefield: 'battlefields', side: 'sideboard' } as const;
  for (const row of input.cards) {
    if (row.zone === 'flex' || row.quantity <= 0) continue;
    addEntry(sections[row.zone], deckCard(row.cardId, sectionName[row.zone]), row.quantity);
  }

  return {
    deck: {
      legend,
      champion,
      main: sortEntries([...sections.main.values()]),
      runes: sortEntries([...sections.rune.values()]),
      battlefields: sortEntries([...sections.battlefield.values()]),
      sideboard: sortEntries([...sections.side.values()]),
    },
    format: input.format ?? 'standard',
    name: input.name,
    issues,
  };
}
